import { gsap } from "gsap";
import { Helper } from "../../../core/utils/Helper";
import { StageLayer } from "@nolimitcity/slot-launcher/bin/StageLayer";
import { IDimOrientation, LayoutDimData, ResizeHelper } from "../../../core/utils/ResizeHelper";
import { ContinueButton } from "@nolimitcity/slot-launcher/bin/gui/buttons/concretebuttons/ContinueButton";
import { SplashWindows } from "./SplashWindows";

export interface SplashView extends PIXI.Container {
    initiated:boolean;
    play(onClicked?:Function): gsap.core.Timeline;
    stop(): gsap.core.Timeline;
    setData?(data:any): void;
    onResize?(): void;
}

export interface SplashPlayConfig {
    view: SplashView;
    ctnBtnFunc?: Function;
    onSplashClick?:Function;
    data?:any;
}

export enum SplashIndex { INTRO = 0, OUTRO = 1, SUMMARY = 2, RESTORE = 3, BREAKTHEBANK = 4 }

export class SplashController extends PIXI.Container implements StageLayer {
    
    private layoutDim: IDimOrientation = {
        landscape: {
            scale: new PIXI.Point(1, 1),
            position: new PIXI.Point(360, 360),
        },
        portrait: {
            scale: new PIXI.Point(1, 1),
            position: new PIXI.Point(360, 360)
        }
    };

    private _continueButton: ContinueButton;
    private _currentView:SplashView;
    private onCtnBtnFunc: Function | undefined;
    private _timeline: gsap.core.Timeline;

    public get button(): ContinueButton { return this._continueButton; }

    constructor() {
        super();
        this.init();
    }

    private init(): void {
        new SplashWindows();

        this.createInteractiveModules();
        this.sortChildren();
        this.visible = false;
        this.onResize();
    }

    private createInteractiveModules(): void {
        this._continueButton = this.addChild(new ContinueButton());

        this._continueButton.enable(false);
        this._continueButton.x -= this._continueButton.width / 2;
        this._continueButton.addClickCallback(() => this.clickCallback())

        this._continueButton.zIndex = 100;
        this._continueButton.alpha = 0;
    }

    private clickCallback(): void {
        this._timeline = Helper.setTimeline(this._timeline, {
            onComplete: () => {
                this.alpha = 1;
                this.visible = false;
                this.removeChild(this._currentView);
                this._currentView = undefined!;
            }
        });

        if(this.onCtnBtnFunc) this.onCtnBtnFunc();

        this._timeline.add(this._currentView.stop(), 0);
        const stopDurr: number = this._timeline.duration();

        this._timeline.fromTo(this, { duration: stopDurr ? stopDurr : 0.25 , alpha: 1 }, { alpha: 0 }, 0);
        this._continueButton.enable(false);
    }

    private setData(view: SplashView, data:any): void {
        view && view.setData != undefined ? view!.setData!(data) : undefined;
    }

    public play(playConf:SplashPlayConfig): gsap.core.Timeline {
        const view = playConf.view;

        if(playConf.data) this.setData(view,playConf.data);

        this.addChildAt(view,0);
        this._currentView = view;

        this.alpha = 1;
        this.visible = true;

        if(playConf.ctnBtnFunc) this.onCtnBtnFunc = playConf.ctnBtnFunc;
        else this.onCtnBtnFunc = null!;

        this._timeline = this._currentView.play(playConf.onSplashClick);

        if (this.onCtnBtnFunc) {
            this._timeline.add([
                () => { 
                    this._continueButton.enable(true)
                    this._continueButton.visible = true;
                },
                gsap.fromTo(this._continueButton, { duration: 0.25, alpha: 0 }, { alpha: 1 })
            ], "-=0.25");
            this._timeline.to(this,{duration:1,repeat:-1});
        }else{
            this._continueButton.enable(false);
        }

        return this._timeline;
    }

    public onResize(): void {

        if(this._currentView){
            this._currentView.onResize ? this._currentView.onResize() : undefined;
        }

        const rezDim: LayoutDimData = ResizeHelper.calcDim(this.layoutDim)

        this.position = rezDim.position;
        this.scale = rezDim.scale;

        this._continueButton.y = ResizeHelper.isLandscape ? 200 : 200;
    }
}
